import { Placement } from 'bootstrap';
import { BaseDialog } from '~/components/dialog/base-dialog';
import { Key } from '~/utils/key';
import { Logger } from '~/utils/logger';

type KeyboardShortcutFunction = () => void;

type KeyboardShortcutElement =
	| HTMLElement
	| HTMLButtonElement
	| HTMLAnchorElement;

interface KeyboardShortcutSubscription {
	context: KeyboardContext;
	keys: readonly Key[];
	shortcutFuncOrElem: KeyboardShortcutFunction | KeyboardShortcutElement;
	condition?: () => boolean;
}

interface KeyboardContextClass {
	// tslint:disable no-reserved-keywords
	constructor: {
		name: string;
	};
}

type KeyboardContext = KeyboardContextClass;

/**
 * NOTE(Jake): 2018-09-26
 * Add an attached() or detached() method to your Aurelia component and subscribe to your
 * shortcuts using the examples below.
 *
 * For a good example of how to use this API, check out:
 * - components\slideout\slideout.ts
 *
 * protected attached() {
 *   // OK!
 *   KeyboardShortcut.subscribe(this, Key.F10, () => this.save());
 *   KeyboardShortcut.subscribe(this, Key.ESCAPE, () => this.close());
 *   KeyboardShortcut.subscribe(this, [Key.SHIFT, Key.F3], this.element.querySelector('[click\\.delegate="showNotesDialog()"]'));
 *   // ERROR! DO NOT DO THIS.
 *   KeyboardShortcut.subscribe(this, [Key.ALT, Key.DELETE], this.showDeleteNotesDialog); // causes runtime error, not fat arrow
 * }
 *
 * // Unsubscribe all key shortcuts
 * protected detached() {
 *   KeyboardShortcut.unsubscribeAll(this);
 * }
 */
export class KeyboardShortcut {
	private static isInitialized: boolean = false;

	// A flat list of all the keyboard shortcut keys, when a key is
	// down, this is iterated in reverse and the first match is executed.
	private static handleList: KeyboardShortcutSubscription[] = [];

	// Group keyboard shortcuts by the instance subscribing to them, this allows
	// us to unsubscribe to all keyboard shortcuts related to that class.
	private static readonly contextToHandleList: Map<
		KeyboardContext,
		KeyboardShortcutSubscription[]
	> = new Map();

	private static isGloballyDisabled: boolean = false;

	public static isDisableShortcuts(): boolean {
		return KeyboardShortcut.isGloballyDisabled;
	}

	/**
	 * Disable all keyboard shortcuts. The shortcut listener is on window.document which makes it difficult to
	 * override.
	 */
	public static setDisableShortcuts(isDisabled: boolean) {
		KeyboardShortcut.isGloballyDisabled = isDisabled;
	}

	/**
	 * keysToString will give a string representation of the keys being pressed.
	 */
	public static keysToString(keys: Key[] | readonly Key[]): string {
		let keyCombination = '';
		for (const key of keys) {
			if (keyCombination !== '') {
				keyCombination += '+';
			}
			keyCombination += key;
		}
		return keyCombination;
	}

	// tslint:disable-next-line:max-line-length
	public static subscribe(
		context: KeyboardContext,
		keysToHold: Key | readonly Key[],
		shortcutFuncOrElemList:
			| KeyboardShortcutFunction
			| Element[]
			| NodeListOf<Element>,
		condition?: () => boolean,
	): void {
		KeyboardShortcut.lazyInit();

		// Check for errors
		let shortcutFuncOrElem: KeyboardShortcutFunction | Element;
		if (typeof shortcutFuncOrElemList === 'function') {
			shortcutFuncOrElem = shortcutFuncOrElemList;
		} else if (shortcutFuncOrElemList.length !== undefined) {
			if (shortcutFuncOrElemList.length === 0) {
				throw new Error(
					'No elements provided, expected 1. Did your querySelectorAll() statement return no results?',
				);
			}
			if (shortcutFuncOrElemList.length > 1) {
				// NOTE(Jake): 2018-10-15 - WEBUI-689
				// We might want to loosen this rule later on to allow the shortcut to apply to
				// multiple elements. For now, disallow.
				Logger.warn('KeyboardShortcut', shortcutFuncOrElemList);
				throw new Error(
					'Multiple elements matched selector, expected 1, see console.warning() above.',
				);
			}
			// NOTE(Jake): 2018-10-15 - WEBUI-689
			// Convert from list of Elements to 1 Element.
			// We want to be given a list of elements so that we can detect cases where two identical elements
			// exist accidentally + to allow us to easily update this library to support multiple elements.
			shortcutFuncOrElem = shortcutFuncOrElemList[0];
		}

		if (
			!(
				shortcutFuncOrElem instanceof HTMLButtonElement ||
				shortcutFuncOrElem instanceof HTMLInputElement ||
				shortcutFuncOrElem instanceof HTMLAnchorElement ||
				typeof shortcutFuncOrElem === 'function'
			)
		) {
			if (typeof shortcutFuncOrElem === 'object') {
				// tslint:disable-next-line:max-line-length
				throw new Error(
					'Invalid subscription function/element object: ' +
						shortcutFuncOrElem.constructor.name +
						', can only pass a function, <button>, <input> or <a> tag element.',
				);
			} else {
				// tslint:disable-next-line:max-line-length
				throw new Error(
					'Invalid subscription function/element type: ' +
						typeof shortcutFuncOrElem +
						', can only pass a function, <button>, <input> or <a> tag element.',
				);
			}
		}

		if (DEVELOPMENT) {
			// Developer Warnings
			if (
				shortcutFuncOrElem instanceof HTMLAnchorElement &&
				shortcutFuncOrElem.getAttribute('role') !== 'button'
			) {
				// tslint:disable-next-line:max-line-length
				Logger.warn(
					'KeyboardShortcut',
					shortcutFuncOrElem,
					' is not a <button> or <input>. This should be changed to <button class="btn btn-link" type="button"/> for correctness. At the minimum, it should have role="button" attribute for screen readers.',
				);
			}

			if (
				shortcutFuncOrElem instanceof HTMLButtonElement &&
				shortcutFuncOrElem.getAttribute('type') !== 'button' &&
				!shortcutFuncOrElem.getAttribute('type')
			) {
				// tslint:disable-next-line:max-line-length
				Logger.warn(
					'KeyboardShortcut',
					shortcutFuncOrElem,
					' missing type="button" attribute. Without this attribute, the button will default to submitting a form.',
				);
			}

			// maybetodo(Jake): 2018-09-21
			// Add DEVELOPMENT only code check here to ensure a user doesn't accidentally
			// subscribe the same keyboard events more than once / detaches them correctly
		}

		// Build key combination string
		const keys: readonly Key[] =
			keysToHold instanceof Array ? keysToHold : [keysToHold];
		const keyCombination = KeyboardShortcut.keysToString(keys);

		// Subscribe to key event
		let handlesForContext =
			KeyboardShortcut.contextToHandleList.get(context);
		if (!handlesForContext) {
			handlesForContext = [];
			KeyboardShortcut.contextToHandleList.set(
				context,
				handlesForContext,
			);
		}
		const handle: KeyboardShortcutSubscription = {
			context,
			keys,
			shortcutFuncOrElem,
			condition,
		};
		handlesForContext.push(handle);
		KeyboardShortcut.handleList.push(handle);

		if (shortcutFuncOrElem instanceof HTMLElement) {
			this.addTooltip(shortcutFuncOrElem, keyCombination, context);
		}

		Logger.debug('KeyboardShortcut', 'subscribe', keysToHold, context);
	}

	public static isSubscribed(context: KeyboardContext): boolean {
		const { contextToHandleList } = KeyboardShortcut;
		const handlesForContext = contextToHandleList.get(context);
		return !handlesForContext ? false : true;
	}

	public static unsubscribeAll(context: KeyboardContext): void {
		const { contextToHandleList } = KeyboardShortcut;
		const handlesForContext = contextToHandleList.get(context);
		if (!handlesForContext) {
			throw new Error(
				'Cannot call unsubscribeAll() if you have not called subscribe()',
			);
		}

		// Remove tooltip
		handlesForContext.forEach((handle) => {
			const shortcutFuncOrElem = handle.shortcutFuncOrElem;
			if (shortcutFuncOrElem instanceof HTMLElement) {
				$(shortcutFuncOrElem).tooltip('dispose');
				const originalTitle = shortcutFuncOrElem.getAttribute(
					'data-ks-original-title',
				);
				if (!originalTitle) {
					shortcutFuncOrElem.removeAttribute('title');
				} else {
					shortcutFuncOrElem.setAttribute('title', originalTitle);
				}
			}
		});

		KeyboardShortcut.handleList = KeyboardShortcut.handleList.filter(
			(subscription) => {
				return handlesForContext.indexOf(subscription) < 0;
			},
		);
		handlesForContext.length = 0;
		contextToHandleList.delete(context);

		Logger.debug(
			'KeyboardShortcut',
			'unsubscribeAll',
			context,
			KeyboardShortcut.handleList,
		);
	}

	private static onKeydown(event: KeyboardEvent): void {
		if (KeyboardShortcut.isDisableShortcuts()) {
			return;
		}

		let hasPressedKeyboardShortcut = false;
		let lastContext: KeyboardContext | undefined;

		// NOTE(Jake): 2018-09-24
		// The most recently subscribed key events will get executed first
		// and take precedence over older subscriptions.
		KeyboardShortcut.handleList
			.slice(0)
			.reverse()
			.forEach((subscription) => {
				if (hasPressedKeyboardShortcut) {
					// Only allow one keyboard shortcut to be activated
					// per callback.
					return;
				}

				// NOTE(Jake): 2018-09-24
				// If we are switching from a dialog/modal context to anything else
				// don't allow those shortcuts to be processed.
				// ie. Stops allows pressing of buttons outside of a modal / slideout.
				//
				// The specific case I tested for this was that if I open a slideout panel that allows
				// you to open notes with F3, it should not allow you infinitely open the
				// notes modal by by pressing F3.
				if (lastContext !== subscription.context) {
					if (lastContext !== undefined) {
						if (lastContext instanceof BaseDialog) {
							hasPressedKeyboardShortcut = true;
							return;
						}
					}
					lastContext = subscription.context;
				}

				// Save all keys that are being pressed during event
				let eventMatchesShortcut: boolean = false;
				const eventKeys: string[] = [event.key];
				if (event.shiftKey) {
					eventKeys.push(Key.SHIFT);
				}
				if (event.ctrlKey) {
					eventKeys.push(Key.CONTROL);
				}
				if (event.altKey) {
					eventKeys.push(Key.ALT);
				}

				// If number of keys pressed during event equals number of keys in shortcut, check that every key matches
				if (eventKeys.length === subscription.keys.length) {
					eventMatchesShortcut = subscription.keys.every(
						(shortcutKey) =>
							eventKeys.includes(String(shortcutKey)),
					);
				}

				// If pressing all required keys, fire the function
				if (eventMatchesShortcut) {
					// Ignore Key.ENTER shortcut if we're currently focused on a <button>.
					// We do this because ENTER == pressing a button.
					if (
						subscription.keys.length === 1 &&
						subscription.keys[0] === Key.ENTER &&
						document.activeElement &&
						document.activeElement.tagName === 'BUTTON'
					) {
						return;
					}
					// If there is a condition for executing the shortcut and it returns
					// false, ignore execution.
					if (subscription.condition && !subscription.condition()) {
						// NOTE: Jake: 2019-07-17 - WEBUI-2246
						// This was implemented so that `event.preventDefault()` is not executed
						// if PAGE_DOWN/PAGE_UP buttons are pressed inside the slideout as we want
						// them to control scroll behaviour in that context.
						return;
					}

					event.preventDefault();
					let isDisabled = false;
					if (subscription.shortcutFuncOrElem instanceof Element) {
						isDisabled = Boolean(
							subscription.shortcutFuncOrElem.getAttribute(
								'disabled',
							),
						);
						if (!isDisabled) {
							subscription.shortcutFuncOrElem.click();
						}
					} else if (
						typeof subscription.shortcutFuncOrElem === 'function'
					) {
						subscription.shortcutFuncOrElem();
					} else {
						throw new Error(
							'Invalid subscription type: ' +
								typeof subscription.shortcutFuncOrElem,
						);
					}
					hasPressedKeyboardShortcut = true;
					Logger.log(
						'KeyboardShortcut::onKeydown',
						eventKeys.length,
						subscription,
						'isDisabled',
						isDisabled,
					);
				}
			});
	}

	private static lazyInit(): void {
		if (!KeyboardShortcut.isInitialized) {
			window.document.addEventListener(
				'keydown',
				KeyboardShortcut.onKeydown,
				{
					capture: true,
					passive: false,
				},
			);
			KeyboardShortcut.isInitialized = true;
		}
	}

	public static addTooltip(
		shortcutElement: Element,
		keyCombination: string,
		context: KeyboardContext,
	) {
		if (DEVELOPMENT && $(shortcutElement).data('bs.tooltip')) {
			if (shortcutElement.getAttribute('data-toggle') === 'tooltip') {
				Logger.warn(
					'KeyboardShortcut',
					keyCombination,
					'need to remove "data-toggle=tooltip" from element',
					shortcutElement,
				);
			} else {
				const error =
					'Bootstrap tooltip binding is already applied by other user-code. This other code needs to be removed for this to work.';
				// tslint:disable-next-line:max-line-length
				Logger.warn(
					'KeyboardShortcut',
					keyCombination,
					error,
					shortcutElement,
				);
				if (DEVELOPMENT) {
					// NOTE: Jake: 2019-08-06
					// If you hit this in DEVELOPMENT. Please fix the problem.
					throw new Error(error);
				}
			}
		}

		let title = shortcutElement.getAttribute('title');
		if (!title) {
			title = shortcutElement.textContent;
			// NOTE(Jake): 2018-10-16 - WEBUI-689
			// Only keep alphanumeric characters, this was added to strip the "x" character
			// from "DialogWithTabs". Intent here is FontAwesome/etc text content should be stripped.
			// NOTE: Jake: 2019-08-28 - WEBUI-2407
			// Disabled as we don't use Material Icons anymore, so I don't think we need it anymore.
			// This was stripping middle space in the phrase "Add Record", so this logic needs to go.
			// title = title.replace(/[^a-zA-Z0-9]+/g, '');
		} else {
			shortcutElement.setAttribute('data-ks-original-title', title);
		}
		title = title.trim();
		title += ' (' + keyCombination + ')';
		shortcutElement.setAttribute('title', title);
		const tooltipOptions: Bootstrap.TooltipOption = {
			delay: {
				show: 500,
				hide: 100,
			},
		};
		const tooltipPlacement: Placement = (context as any)
			.footerTooltipPlacement;
		if (tooltipPlacement) {
			tooltipOptions.placement = tooltipPlacement;
		}
		$(shortcutElement).tooltip(tooltipOptions);
	}
}
